import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { fmtEuro, fmtDateTime } from "@/lib/format";
import { PageHeader, Empty } from "@/components/Common";
import { ValidationQueue } from "@/components/ValidationQueue";
import { BillingExport } from "@/components/BillingExport";
import { TechLeaderboard } from "@/components/TechLeaderboard";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const BulkList = ({ status, action, label, testId }) => {
  const { companyId, nameOf } = useData();
  const { can } = useAuth();
  const [orders, setOrders] = useState([]);
  const [sel, setSel] = useState([]);
  const load = useCallback(() => api.get("/orders", { params: { status, company_id: companyId || undefined } }).then((r) => { setOrders(r.data); setSel([]); }), [status, companyId]);
  useEffect(() => { load(); }, [load]);
  const run = async () => {
    try {
      const { data } = await api.post(`/production/${action}`, { order_ids: sel });
      toast.success(`${data.updated} ordini aggiornati`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!orders.length) return <Empty text="Nessun ordine in questo stato" testId={`${testId}-empty`} />;
  return (
    <div data-testid={testId}>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{sel.length} selezionati</span>
        <Button disabled={!sel.length || !can("production:bill")} onClick={run} data-testid={`${testId}-action`}>{label}</Button>
      </div>
      <div className="overflow-x-auto border bg-white">
        <Table>
          <TableHeader><TableRow className="bg-muted/50"><TableHead className="w-10"><Checkbox checked={sel.length === orders.length} onCheckedChange={(c) => setSel(c ? orders.map((o) => o.id) : [])} data-testid={`${testId}-select-all`} /></TableHead>
            <TableHead>Ordine</TableHead><TableHead>Committente</TableHead><TableHead>Titolare / Esecutrice</TableHead><TableHead className="text-right">Valore validato</TableHead></TableRow></TableHeader>
          <TableBody>
            {orders.map((o) => (
              <TableRow key={o.id}>
                <TableCell><Checkbox checked={sel.includes(o.id)} onCheckedChange={(c) => setSel(c ? [...sel, o.id] : sel.filter((x) => x !== o.id))} data-testid={`${testId}-check-${o.external_order_id}`} /></TableCell>
                <TableCell className="font-mono text-sm font-semibold">{o.external_order_id}</TableCell>
                <TableCell className="text-sm">{nameOf("parties", o.customer_id)}</TableCell>
                <TableCell className="text-sm">{nameOf("companies", o.contract_company_id, "code")} / {nameOf("companies", o.executing_company_id, "code")}</TableCell>
                <TableCell className="text-right font-mono">{fmtEuro(o.validated_amount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const GROUPS = { company: ["Società titolare", "companies"], executing: ["Società esecutrice", "companies"], project: ["Commessa", "projects"], customer: ["System Integrator", "parties"],
  manager: ["Gestore infrastruttura", "parties"], region: ["Regione", null], team: ["Squadra", "teams"], employee: ["Tecnico", "employees"], service: ["Settore", null] };

const Summary = () => {
  const { companyId, nameOf } = useData();
  const [groupBy, setGroupBy] = useState("company");
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/production/summary", { params: { group_by: groupBy, company_id: companyId || undefined } }).then((r) => setData(r.data)).catch(() => {});
  }, [groupBy, companyId]);
  if (!data) return null;
  const [label, kind] = GROUPS[groupBy];
  const t = data.totals;
  return (
    <div data-testid="production-summary">
      <div className="mb-4 grid grid-cols-2 gap-4 md:grid-cols-6">
        {[["Eseguita", t.executed], ["Validata", t.validated], ["Fatturabile", t.billable], ["Costi diretti", t.cost], ["Margine", t.margin]].map(([l, v]) => (
          <div key={l} className="border bg-white p-4"><div className="micro-label">{l}</div><div className="mt-2 font-mono text-xl font-semibold">{fmtEuro(v)}</div></div>
        ))}
        <div className="border bg-white p-4"><div className="micro-label">Margine %</div><div className="mt-2 font-mono text-xl font-semibold">{t.margin_pct ?? "—"}%</div></div>
      </div>
      <div className="mb-3 flex items-center gap-3">
        <span className="micro-label">Raggruppa per</span>
        <Select value={groupBy} onValueChange={setGroupBy}>
          <SelectTrigger className="w-56 bg-white" data-testid="summary-group-by"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(GROUPS).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto border bg-white">
        <Table>
          <TableHeader><TableRow className="bg-muted/50"><TableHead>{label}</TableHead><TableHead className="text-right">Eseguita</TableHead><TableHead className="text-right">Validata</TableHead><TableHead className="text-right">Fatturabile</TableHead><TableHead className="text-right">Ore</TableHead><TableHead className="text-right">Costi</TableHead><TableHead className="text-right">Margine</TableHead><TableHead className="text-right">%</TableHead></TableRow></TableHeader>
          <TableBody>
            {data.rows.map((r) => (
              <TableRow key={r.key || "none"}>
                <TableCell className="text-sm font-medium">{kind ? nameOf(kind, r.key) : r.key || "—"}</TableCell>
                {["executed", "validated", "billable"].map((f) => <TableCell key={f} className="text-right font-mono text-sm">{fmtEuro(r[f])}</TableCell>)}
                <TableCell className="text-right font-mono text-sm">{r.hours}</TableCell>
                <TableCell className="text-right font-mono text-sm text-red-700">{fmtEuro(r.cost)}</TableCell>
                <TableCell className="text-right font-mono text-sm font-semibold">{fmtEuro(r.margin)}</TableCell>
                <TableCell className="text-right font-mono text-sm">{r.margin_pct ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">Il margine considera ricavi da listino e costo orario diretto dei tecnici; esclude costi indiretti non imputati.</p>
    </div>
  );
};

const Intercompany = () => {
  const { nameOf } = useData();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/production/intercompany").then((r) => setRows(r.data)).catch(() => {}); }, []);
  if (!rows.length) return <Empty text="Nessun movimento intercompany" testId="intercompany-empty" />;
  return (
    <div className="overflow-x-auto border bg-white" data-testid="intercompany-table">
      <Table>
        <TableHeader><TableRow className="bg-muted/50"><TableHead>Data</TableHead><TableHead>Ordine</TableHead><TableHead>Tipo</TableHead><TableHead>Da</TableHead><TableHead>A</TableHead><TableHead className="text-right">Importo</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell className="font-mono text-xs">{fmtDateTime(r.created_at)}</TableCell><TableCell className="font-mono text-sm">{r.external_order_id}</TableCell>
              <TableCell className="text-sm">{r.kind === "LABOR" ? "Prestazione personale" : "Produzione eseguita per titolare"}</TableCell>
              <TableCell className="text-sm">{nameOf("companies", r.from_company_id, "code")}</TableCell><TableCell className="text-sm">{nameOf("companies", r.to_company_id, "code")}</TableCell>
              <TableCell className="text-right font-mono">{fmtEuro(r.amount)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default function ProductionControl() {
  const { can } = useAuth();
  return (
    <div data-testid="production-page">
      <PageHeader eyebrow="Schermata E · 06" title="Controllo produzione" />
      <Tabs defaultValue="validate">
        <TabsList className="mb-4 rounded-none border bg-white">
          <TabsTrigger value="validate" data-testid="production-tab-validate">Da validare</TabsTrigger>
          <TabsTrigger value="validated" data-testid="production-tab-validated">Validati</TabsTrigger>
          <TabsTrigger value="billable" data-testid="production-tab-billable">Contabilizzabili</TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="production-tab-leaderboard">Classifica tecnici</TabsTrigger>
          {can("finance:read") && <TabsTrigger value="summary" data-testid="production-tab-summary">Ricavi, costi e margini</TabsTrigger>}
          {can("finance:read") && <TabsTrigger value="ic" data-testid="production-tab-intercompany">Intercompany</TabsTrigger>}
          {can("finance:read") && <TabsTrigger value="billing" data-testid="production-tab-billing">Export fatturazione</TabsTrigger>}
        </TabsList>
        <TabsContent value="validate"><ValidationQueue /></TabsContent>
        <TabsContent value="validated"><BulkList status="VALIDATED" action="billable" label="Ammetti a valorizzazione (contabilizzabile)" testId="validated-list" /></TabsContent>
        <TabsContent value="billable"><BulkList status="BILLABLE" action="close" label="Chiudi ciclo operativo" testId="billable-list" /></TabsContent>
        <TabsContent value="leaderboard"><TechLeaderboard /></TabsContent>
        {can("finance:read") && <TabsContent value="summary"><Summary /></TabsContent>}
        {can("finance:read") && <TabsContent value="ic"><Intercompany /></TabsContent>}
        {can("finance:read") && <TabsContent value="billing"><BillingExport /></TabsContent>}
      </Tabs>
    </div>
  );
}
